import { CONTENT_CATEGORIES, keyOf } from './db';
import contentPack from './content-pack.json';

const cursorKey = (cat) => `cursor:${cat}`;

// Categories dont l'ordre d'affichage compte (preserve l'ordre source du pack).
const ORDERED = ['modes', 'progressions', 'moods'];

// Seed IndexedDB depuis le content pack bundle si la base est vide (cold-start offline).
// La baseline est livree avec l'app : utilisable des le premier lancement sans reseau.
export async function seedIfEmpty(db) {
  if ((await db.count('modes')) > 0) return false;
  for (const cat of CONTENT_CATEGORIES) {
    const rows = contentPack[cat] || [];
    if (!rows.length) continue;
    const tx = db.transaction(cat, 'readwrite');
    for (const row of rows) await tx.store.put(row);
    await tx.done;
  }
  return true;
}

// Lit tous les stores en une structure pack-like pour hydrater le miroir.
// Reordonne modes/progressions/moods selon l'ordre source (IndexedDB rend par cle).
export async function readAllStores(db) {
  const out = {};
  for (const cat of CONTENT_CATEGORIES) {
    const rows = await db.getAll(cat);
    if (ORDERED.includes(cat)) {
      const idx = new Map((contentPack[cat] || []).map((r, i) => [r.id, i]));
      rows.sort((a, b) => (idx.get(a.id) ?? 1e9) - (idx.get(b.id) ?? 1e9));
    }
    out[cat] = rows;
  }
  return out;
}

// Synchronise le content pack vers IndexedDB : manifeste -> delta -> cache.
// Offline : ne touche pas au reseau, l'UI lit le cache existant.
// Online : 1 requete manifeste, puis delta SEULEMENT pour les categories plus recentes
// que le curseur local (une categorie inchangee = zero requete delta).
export async function syncContent(db, { apiUrl, online, locale = 'en', fetchImpl = fetch } = {}) {
  if (!online) {
    return { offline: true, synced: [], skipped: [...CONTENT_CATEGORIES] };
  }

  const manifest = await fetchImpl(`${apiUrl}/api/content/manifest`).then((r) => r.json());
  const synced = [];
  const skipped = [];

  for (const cat of CONTENT_CATEGORIES) {
    const serverVersion = manifest.categories?.[cat] ?? null;
    if (serverVersion == null) {
      skipped.push(cat); // categorie vide cote serveur
      continue;
    }

    const cursor = await db.get('meta', cursorKey(cat));
    if (cursor && new Date(cursor) >= new Date(serverVersion)) {
      skipped.push(cat); // a jour : aucune requete delta
      continue;
    }

    const since = cursor || '';
    const localeParam = cat === 'texts' ? `&locale=${encodeURIComponent(locale)}` : '';
    const url = `${apiUrl}/api/content/delta?cat=${cat}&since=${encodeURIComponent(since)}${localeParam}`;
    const { rows } = await fetchImpl(url).then((r) => r.json());

    const tx = db.transaction(cat, 'readwrite');
    for (const row of rows) {
      if (row.deleted) {
        await tx.store.delete(keyOf(cat, row));
      } else {
        await tx.store.put(row);
      }
    }
    await tx.done;

    await db.put('meta', serverVersion, cursorKey(cat));
    synced.push(cat);
  }

  return { offline: false, synced, skipped };
}
